/**
 * Safely converts a value to a numeric enum value.
 * Only works with numeric enums and always returns a number.
 *
 * @param enumObject - The numeric enum object (e.g., SignerRole)
 * @param value - The value to convert (can be number, string enum key, or the enum value itself)
 * @returns The numeric enum value if valid, undefined otherwise
 *
 * @example
 * // Numeric enum
 * enum SignerRole { Buyer = 0, Seller = 1 }
 * toEnumValue(SignerRole, 0) // Returns 0
 * toEnumValue(SignerRole, "buyer") // Returns 0
 * toEnumValue(SignerRole, "Buyer") // Returns 0
 */
export function toEnumValue<T extends { [key: string]: number | string }>(
  enumObject: T,
  value: string | number | T[keyof T] | undefined | null
): number | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  // Get all numeric enum values (filter out numeric keys)
  const enumValues = Object.values(enumObject).filter(
    (v) => typeof v === "number"
  ) as number[];

  // Check if value is already a valid numeric enum value
  if (typeof value === "number") {
    for (const enumValue of enumValues) {
      if (enumValue === value) {
        return value;
      }
    }
  }

  // If value is a string, try to find matching enum key (case-insensitive)
  if (typeof value === "string") {
    const normalized = value.toLowerCase().trim();

    // Try to find enum key by case-insensitive match
    for (const key in enumObject) {
      if (Object.prototype.hasOwnProperty.call(enumObject, key)) {
        const enumKey = key.toLowerCase();
        const enumVal = enumObject[key];

        // Check if the normalized string matches the enum key
        if (enumKey === normalized && typeof enumVal === "number") {
          return enumVal;
        }
      }
    }
  }

  return undefined;
}
